import { useState } from "react";
import { PHASE, PHASE_TEXT } from "../../common/constants.ts";
import { DataTable } from "../components/DataTable/index.tsx";
import { MoreLinks } from "../components/MoreLinks.tsx";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { helpers } from "../util/helpers.ts";
import { getCols } from "../../common/getCols.ts";
import { useLocal } from "../util/local.ts";
import type { Phase, View } from "../../common/types.ts";
import { wrappedMood } from "../components/Mood.tsx";
import {
	wrappedContractAmount,
	wrappedContractExp,
} from "../components/contract.tsx";
import { wrappedPlayerNameLabels } from "../components/PlayerNameLabels.tsx";
import { range } from "../../common/utils.ts";
import { contractAmountForYears } from "../../common/contractYearAmount.ts";
import type { DropdownOption } from "../hooks/useDropdownOptions.tsx";
import type { FreeAgentTransaction } from "../../worker/views/freeAgents.ts";
import {
	type DataTableHandle,
	type DataTableRow,
} from "../components/DataTable/index.tsx";
import { NegotiateButtons } from "../components/NegotiateButtons.tsx";
import { RosterComposition } from "../components/RosterComposition.tsx";
import { RosterSalarySummary } from "../components/RosterSalarySummary.tsx";
import {
	NegotiationModal,
	useNegotiaionModal,
} from "../components/NegotiationModal.tsx";
import { toWorker } from "../util/toWorker.ts";
import { logEvent } from "../util/logEvent.ts";

const useSeasonsFreeAgents = () => {
	const { phase, season, startingSeason } = useLocal([
		"phase",
		"season",
		"startingSeason",
	]);

	// Decrease season by 1, since "free agent season" starts in the previous calendar year
	const minFreeAgencySeason = startingSeason - 1;

	// These are 1 lower than you'd expect, because there's also a "current" entry added below
	const maxFreeAgencySeason = phase >= PHASE.PLAYOFFS ? season - 1 : season - 2;

	const options: DropdownOption[] = range(
		minFreeAgencySeason,
		maxFreeAgencySeason + 1,
	).map((freeAgencySeason) => {
		let value;
		if (freeAgencySeason >= -10 && freeAgencySeason < 10) {
			value = `${freeAgencySeason}-${freeAgencySeason + 1}`;
		} else {
			value = `${freeAgencySeason}-${String((freeAgencySeason + 1) % 100).padStart(2)}`;
		}

		return {
			key: freeAgencySeason,
			value,
		};
	});

	options.push({
		key: "current",
		value: "Current",
	});

	options.reverse();

	return options;
};

const signedFreeAgentWrapped = (
	freeAgentTransaction: FreeAgentTransaction & {
		abbrev: string;
	},
	freeAgencySeason: number,
	season: number | "current",
	phase: Phase,
) => {
	let rosterSeason;

	if (season === "current" && phase >= PHASE.PLAYOFFS) {
		// Link to current season roster, because there is no next season roster
		rosterSeason = freeAgencySeason;
	} else {
		// Link to next season roster, because freeAgencySeason starts after the regular season ends
		rosterSeason = freeAgencySeason + 1;
	}

	return {
		value: (
			<>
				<a
					href={helpers.leagueUrl([
						"roster",
						`${freeAgentTransaction.abbrev}_${freeAgentTransaction.tid}`,
						rosterSeason,
					])}
				>
					{freeAgentTransaction.abbrev}
				</a>
				, {PHASE_TEXT[freeAgentTransaction.phase]}
			</>
		),
		searchValue: `${freeAgentTransaction.abbrev}, ${PHASE_TEXT[freeAgentTransaction.phase]}`,
	};
};

const FreeAgents = ({
	capSpace,
	challengeNoFreeAgents,
	freeAgencySeason,
	minContractLength,
	maxContractLength,
	mp,
	numRosterSpots,
	payroll,
	players,
	season,
	stats,
	type,
	userPlayers,
}: View<"freeAgents">) => {
	const seasonsFreeAgents = useSeasonsFreeAgents();
	// Multiplayer blind-bid auction state (per-row bid inputs)
	const [mpAmounts, setMpAmounts] = useState<Record<number, string>>({});
	const [mpYears, setMpYears] = useState<Record<number, string>>({});
	const [mpBusy, setMpBusy] = useState(false);

	useTitleBar({
		title: "Free Agents",
		dropdownView: "free_agents",
		dropdownFields: { typeFreeAgents: type, seasonsFreeAgents: season },
		dropdownCustomOptions: {
			seasonsFreeAgents,
		},
	});

	const {
		challengeNoRatings,
		gameSimInProgress,
		minContract,
		maxContract,
		phase,
		salaryCapType,
		season: leagueSeason,
		spectator,
	} = useLocal([
		"challengeNoRatings",
		"gameSimInProgress",
		"minContract",
		"maxContract",
		"phase",
		"salaryCapType",
		"season",
		"spectator",
	]);

	// The asking price a free agent wants for a given number of years, matching
	// vanilla's contract options: cheapest at his preferred length, more for
	// anything longer or shorter. Amounts are in thousands (BBGM contract units).
	const mpAskingThousands = (p: (typeof players)[number], years: number) =>
		contractAmountForYears({
			baseAmount: Math.round(p.contract.amount * 1000),
			desiredExp: p.contract.exp,
			ovr: p.ratings.ovr,
			years,
			season: leagueSeason,
			minContractLength,
			maxContractLength,
			minContract,
			maxContract,
		});
	// Default the year selector to the player's preferred length (his cheapest)
	const mpSelectedYears = (pid: number, exp: number) =>
		mpYears[pid] !== undefined
			? Number.parseInt(mpYears[pid]!)
			: Math.min(
					Math.max(exp - leagueSeason, minContractLength),
					maxContractLength,
				);

	const [dataTableHandle, setDataTableHandle] =
		useState<DataTableHandle | null>(null);

	const negotiationModal = useNegotiaionModal();

	if (
		((phase > PHASE.AFTER_TRADE_DEADLINE && phase <= PHASE.RESIGN_PLAYERS) ||
			phase === PHASE.FANTASY_DRAFT ||
			phase === PHASE.EXPANSION_DRAFT) &&
		season === "current"
	) {
		return (
			<div>
				<MoreLinks type="freeAgents" page="free_agents" />
				<p>You're not allowed to sign free agents now.</p>
				<p>
					Free agents can only be signed before the playoffs or after players
					are re-signed.
				</p>
			</div>
		);
	}

	const askingForText = "Asking For";
	const colKeys = [
		"Name",
		"Pos",
		"Age",
		"Ovr",
		"Pot",
		...stats.map((stat) => `stat:${stat}`),
		"Mood",
		askingForText,
		"Exp",
		"Actions",
	];
	const cols = getCols(colKeys, {
		Actions: {
			width: "1px",
		},
	});

	const showShowPlayersAffordButton = salaryCapType !== "none";

	// These are used in showAffordablePlayersFilterApplied calculation every render, and then also in toggleShowAfforablePlayers when that is called
	let askingForIndex = -1;
	let askingForFilter = "";

	let showAffordablePlayersFilterApplied = false;
	if (showShowPlayersAffordButton && dataTableHandle) {
		askingForIndex = colKeys.lastIndexOf(askingForText);
		if (capSpace * 1000 > minContract && !challengeNoFreeAgents) {
			askingForFilter = `<${capSpace}`;
		} else {
			askingForFilter = `<${minContract / 1000}`;
		}

		const enableFilters = dataTableHandle.getEnableFilters();
		if (enableFilters) {
			const filters = dataTableHandle.getFilters();
			showAffordablePlayersFilterApplied =
				filters[askingForIndex] === askingForFilter;
		}
	}

	const toggleShowAfforablePlayers = () => {
		if (dataTableHandle) {
			const enableFilters = dataTableHandle.getEnableFilters();

			// Start from either the current filters (if they are shown/enabled) or no filters at all
			const filters: string[] = enableFilters
				? [...dataTableHandle.getFilters()]
				: new Array(cols.length).fill("");

			// If we currently have this exact filter set, delete it. Otherwise, add it
			let newEnableFilters = true;
			if (filters[askingForIndex] === askingForFilter) {
				filters[askingForIndex] = "";

				// If no other filters are applied, hide filter bar
				if (filters.every((filter) => filter === "")) {
					newEnableFilters = false;
				}
			} else {
				filters[askingForIndex] = askingForFilter;
			}

			dataTableHandle.setFilters(filters, newEnableFilters);
		}
	};

	const playerInfoSeason =
		freeAgencySeason +
		(season === "current" && phase < PHASE.FREE_AGENCY ? 1 : 0);

	// Multiplayer auction actions: secret bids via the server, resolved when
	// everyone is ready
	const mpAuction = !!mp?.faOpen && season === "current";
	const myBidByPid = new Map((mp?.myBids ?? []).map((b) => [b.pid, b]));
	const mpLogError = (error: unknown) => {
		logEvent({
			type: "error",
			text: error instanceof Error ? error.message : String(error),
			saveToDb: false,
		});
	};
	const mpBid = async (pid: number, asking: number, years: number) => {
		if (!mp) {
			return;
		}
		const raw = mpAmounts[pid];
		const amountThousands =
			raw === undefined || raw === ""
				? asking
				: Math.round(Number.parseFloat(raw) * 1000);
		if (!Number.isFinite(amountThousands) || amountThousands <= 0) {
			mpLogError(new Error("Enter a valid bid amount"));
			return;
		}
		setMpBusy(true);
		try {
			await toWorker("main", "mpPlaceFaBid", {
				leagueId: mp.leagueId,
				pid,
				amount: amountThousands,
				years,
			});
		} catch (error) {
			mpLogError(error);
		}
		setMpBusy(false);
	};
	const mpWithdraw = async (pid: number) => {
		if (!mp) {
			return;
		}
		setMpBusy(true);
		try {
			await toWorker("main", "mpWithdrawFaBid", { leagueId: mp.leagueId, pid });
		} catch (error) {
			mpLogError(error);
		}
		setMpBusy(false);
	};
	const mpResolve = async () => {
		if (!mp) {
			return;
		}
		setMpBusy(true);
		try {
			await toWorker("main", "mpResolveFaRound", mp.leagueId);
		} catch (error) {
			mpLogError(error);
		}
		setMpBusy(false);
	};
	// First-come-first-serve direct signing (outside the auction), at the
	// player's asking price for the chosen number of years.
	const mpDirectSign = !!mp?.directSignOpen && season === "current";
	const mpSign = async (pid: number, years: number) => {
		if (!mp) {
			return;
		}
		setMpBusy(true);
		try {
			const result = await toWorker("main", "mpSignFreeAgent", {
				leagueId: mp.leagueId,
				pid,
				years,
			});
			if (result.status === "already_signed") {
				mpLogError(new Error("Another team just signed this player."));
			} else if (result.status === "refuses") {
				mpLogError(
					new Error(
						"This player refuses to sign with you, no matter what you offer.",
					),
				);
			} else if (result.status === "over_cap") {
				mpLogError(
					new Error(
						"You don't have enough cap room to sign this player at their asking price.",
					),
				);
			} else if (result.status === "roster_full") {
				mpLogError(new Error("Your roster is already full."));
			}
		} catch (error) {
			mpLogError(error);
		}
		setMpBusy(false);
	};

	const rows: DataTableRow[] = players.map((p) => {
		// Multiplayer: the price this free agent asks scales with the number of
		// years being offered (cheapest at his preferred length), and he can flat
		// out refuse to sign with your team - both matching vanilla BBGM.
		const mpActive =
			!!mp &&
			season === "current" &&
			p.freeAgentType === "available" &&
			!!(p as any).mood?.user;
		const mpYrs = mpActive ? mpSelectedYears(p.pid, p.contract.exp) : 0;
		const mpAskingT = mpActive ? mpAskingThousands(p, mpYrs) : 0;
		const mpRefuses = mpActive && !(p as any).mood.user.willing;
		return {
			key: p.pid,
			metadata: {
				type: "player",
				pid: p.pid,
				season: playerInfoSeason,
				playoffs: "regularSeason",
			},
			data: [
				wrappedPlayerNameLabels({
					pid: p.pid,
					injury: p.injury,
					jerseyNumber: p.jerseyNumber,
					skills: p.ratings.skills,
					defaultWatch: p.watch,
					firstName: p.firstName,
					firstNameShort: p.firstNameShort,
					lastName: p.lastName,
					season: playerInfoSeason,
				}),
				p.ratings.pos,
				p.age,
				!challengeNoRatings ? p.ratings.ovr : null,
				!challengeNoRatings ? p.ratings.pot : null,
				...stats.map((stat) => helpers.roundStat(p.stats[stat], stat)),
				p.freeAgentType === "available"
					? wrappedMood({
							defaultType: "user",
							maxWidth: true,
							p,
						})
					: undefined,
				wrappedContractAmount(
					p,
					mpActive ? mpAskingT / 1000 : p.contract.amount,
				),
				wrappedContractExp(p),
				p.freeAgentType === "available"
					? mpAuction
						? (() => {
								const myBid = myBidByPid.get(p.pid);
								if (mpRefuses && !myBid) {
									return {
										value: (
											<span className="text-danger">
												Refuses to sign with you
											</span>
										),
										searchValue: "Refuses",
									};
								}
								return {
									value: myBid ? (
										<>
											<span>
												Bid: {helpers.formatCurrency(myBid.amount / 1000, "M")}
												/yr x {myBid.years}
											</span>
											<button
												type="button"
												className="btn btn-light-bordered btn-xs"
												disabled={mpBusy}
												onClick={() => mpWithdraw(p.pid)}
											>
												Withdraw
											</button>
										</>
									) : (
										<>
											<input
												type="number"
												className="form-control form-control-sm"
												style={{ width: 70 }}
												min={0.1}
												step={0.1}
												placeholder={(mpAskingT / 1000).toFixed(1)}
												value={mpAmounts[p.pid] ?? ""}
												onChange={(event) => {
													setMpAmounts((prev) => ({
														...prev,
														[p.pid]: event.target.value,
													}));
												}}
											/>
											<select
												className="form-select form-select-sm w-auto"
												value={String(mpYrs)}
												onChange={(event) => {
													setMpYears((prev) => ({
														...prev,
														[p.pid]: event.target.value,
													}));
												}}
											>
												{range(minContractLength, maxContractLength + 1).map(
													(y) => (
														<option key={y} value={y}>
															{y}
														</option>
													),
												)}
											</select>
											<button
												type="button"
												className="btn btn-primary btn-xs"
												disabled={mpBusy}
												onClick={() =>
													// mpAskingT is in thousands, like raw contracts
													mpBid(p.pid, mpAskingT, mpYrs)
												}
											>
												Bid
											</button>
										</>
									),
									classNames: "d-flex align-items-center gap-2",
									searchValue: myBid ? "Bid Withdraw" : "Bid",
								};
							})()
						: mpDirectSign && mpRefuses
							? {
									value: (
										<span className="text-danger">
											Refuses to sign with you
										</span>
									),
									searchValue: "Refuses",
								}
							: mpDirectSign
								? {
										value: (
											<div className="d-flex align-items-center gap-2">
												<select
													className="form-select form-select-sm w-auto"
													value={String(mpYrs)}
													onChange={(event) => {
														setMpYears((prev) => ({
															...prev,
															[p.pid]: event.target.value,
														}));
													}}
												>
													{range(minContractLength, maxContractLength + 1).map(
														(y) => (
															<option key={y} value={y}>
																{y}
															</option>
														),
													)}
												</select>
												<button
													type="button"
													className="btn btn-primary btn-xs"
													disabled={mpBusy}
													onClick={() => mpSign(p.pid, mpYrs)}
												>
													Sign
												</button>
											</div>
										),
										classNames: "d-flex align-items-center gap-2",
										searchValue: "Sign",
									}
								: mp && season === "current"
									? {
											value: (
												<span className="text-body-secondary">
													{mp.faOpen ? "Auction only" : "Sign later"}
												</span>
											),
											searchValue: "Auction only",
										}
									: {
											value: (
												<NegotiateButtons
													canGoOverCap={salaryCapType === "none"}
													capSpace={capSpace}
													disabled={gameSimInProgress}
													minContract={minContract}
													onNegotiate={async () => {
														await negotiationModal.negotiate(p.pid);
													}}
													spectator={spectator}
													p={p}
													willingToNegotiate={p.mood.user.willing}
												/>
											),
											classNames: "d-flex align-items-center gap-2",
											searchValue: p.mood.user.willing
												? "Negotiate Sign"
												: "Refuses!",
										}
					: signedFreeAgentWrapped(
							p.freeAgentTransaction,
							freeAgencySeason,
							season,
							phase,
						),
			],
		};
	});

	return (
		<>
			{season === "current" ? (
				<RosterComposition className="float-end mb-3" players={userPlayers} />
			) : null}
			<MoreLinks type="freeAgents" page="free_agents" />
			{season === "current" ? (
				<>
					<RosterSalarySummary
						capSpace={capSpace}
						numRosterSpots={numRosterSpots}
						payroll={payroll}
					/>

					{showShowPlayersAffordButton ? (
						<button
							className="btn btn-secondary mb-3"
							onClick={toggleShowAfforablePlayers}
						>
							{showAffordablePlayersFilterApplied
								? "Show players with any asking price"
								: "Show players you can afford now"}
						</button>
					) : null}
				</>
			) : null}

			{mp && season === "current" ? (
				<div
					className={`alert d-inline-block ${mpAuction ? "alert-primary" : "alert-secondary"}`}
				>
					{mpAuction ? (
						<>
							<b>Free agency auction - one round of secret bids.</b> When
							everyone is done, players sign with the best offer they're willing
							to take; anyone you miss on stays a free agent (the AI tops up
							short rosters during sims). An empty bid amount offers their
							asking price. Bids at the minimum (
							{helpers.formatCurrency(minContract / 1000, "M")}) are always
							cap-legal.
							{mp.myBids.length > 0 ? (
								<>
									{" "}
									You have {mp.myBids.length}{" "}
									{helpers.plural("bid", mp.myBids.length)} in (
									{helpers.formatCurrency(
										mp.myBids.reduce((sum, b) => sum + b.amount, 0) / 1000,
										"M",
									)}{" "}
									committed).
								</>
							) : null}{" "}
							When you're done bidding, ready up on the{" "}
							<a href={helpers.leagueUrl(["multiplayer"])}>Multiplayer</a> page.
							{mp.isRunner && mp.allReady ? (
								<>
									{" "}
									<button
										type="button"
										className="btn btn-sm btn-secondary"
										disabled={mpBusy}
										onClick={mpResolve}
									>
										Resolve Round Now
									</button>
								</>
							) : null}
						</>
					) : (
						<>
							In this multiplayer league, free agents sign through secret
							auction rounds at the <b>Free agency</b> checkpoint. Bidding
							unlocks here when the auction opens.
						</>
					)}
				</div>
			) : null}

			{gameSimInProgress && !spectator ? (
				<p className="text-danger">Stop game simulation to sign free agents.</p>
			) : null}

			{spectator ? (
				<div>
					<div className="alert alert-danger d-inline-block">
						The AI will handle signing free agents in spectator mode.
					</div>
				</div>
			) : challengeNoFreeAgents ? (
				<div>
					<div className="alert alert-danger d-inline-block">
						<b>Challenge Mode:</b> You are not allowed to sign free agents,
						except to minimum contracts.
					</div>
				</div>
			) : null}

			<DataTable
				cols={cols}
				defaultSort={[cols.length - 3, "desc"]}
				defaultStickyCols={window.mobile ? 0 : 1}
				name="FreeAgents"
				pagination
				ref={setDataTableHandle}
				rows={rows}
			/>

			<NegotiationModal {...negotiationModal.props} />
		</>
	);
};

export default FreeAgents;
